import React, { useEffect, useRef, useState } from 'react';
import type { DataGroup, DataItem } from 'vis-timeline';
import { Timeline, TimelineOptions } from 'vis-timeline';
import { AgendaProps } from './Agenda.types';
import './Agenda.scss';
import { Button } from '..';
import dayjs from 'dayjs';
import { generateIsoDateForDateAndTime } from 'shared';
import { DataSet } from 'vis-data';
import 'vis-timeline/styles/vis-timeline-graph2d.min.css';
export const Agenda: React.FC<AgendaProps> = ({
  entryGroups = [],
  addItem,
  start,
  end,
  deleteItem = () => {},
  handleEdit = () => {},
  filterValue,
}) => {
  const [sortingOrder, setSortingOrder] = useState<'asc' | 'desc'>('asc');
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<Timeline | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    timelineRef.current = new Timeline(containerRef.current, new DataSet(), new DataSet(), options);

    return () => {
      timelineRef.current?.destroy();
      timelineRef.current = null;
    };
  }, []);
  /**
   * Timeline to be used to draw the agenda on
   */
  const options: TimelineOptions = {
    verticalScroll: true,
    zoomKey: 'ctrlKey',
    editable: true,
    onRemove: (item) => deleteItem(item.id.toString()),
    onAdd: (newItem) => (addItem ? addItem(newItem) : () => {}),
    orientation: 'top',
    stack: false,
    showWeekScale: true,
    start: start ? start : dayjs().add(-1, 'week').toISOString(),
    end: end ? end : dayjs().add(1, 'week').toISOString(),
    groupTemplate: (group): HTMLElement => {
      const wrapper: HTMLDivElement = document.createElement('div');
      wrapper.className = 'group-wrapper';
      if (!group) return wrapper;
      const name: HTMLDivElement = document.createElement('div');
      name.className = 'col-name';
      name.textContent = group.content;

      const status: HTMLDivElement = document.createElement('div');
      status.className = 'col-type';

      status.textContent =
        entryGroups.find((entryGroup) => entryGroup.id === group.id)?.employmentType ?? '';

      wrapper.appendChild(name);
      wrapper.appendChild(status);

      return wrapper;
    },
    locale: 'de', // needs to be set additionally in the moment instance... WIP
  };

  //useEffect to convert the entryGroups into Datasets to use in the timeline component
  useEffect(() => {
    if (!timelineRef.current) return;
    if (!containerRef.current) return;
    const groups: DataSet<DataGroup> = new DataSet();
    const items: DataSet<DataItem> = new DataSet();
    // loops over swimlanes
    [...entryGroups]
      .sort(
        (groupA, groupB) =>
          groupA.groupName.localeCompare(groupB.groupName) * (sortingOrder === 'asc' ? 1 : -1),
      )
      .forEach((group) => {
        if (
          !filterValue ||
          group.groupName.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase())
        )
          groups.add({
            id: group.id,
            content: group.groupName,
            options: group.options,
          });

        // loops over entries in swimlane
        group.entries.forEach((entry) => {
          items.add({
            content: entry.title,
            id: entry.id,
            start: generateIsoDateForDateAndTime(new Date(entry.start), 0),
            end: generateIsoDateForDateAndTime(new Date(entry.end), 1440),
            group: group.id,
            style: `background: ${entry.background}; 
          color: ${entry.color || ''}; 
          border: ${entry.border}; 
          border-radius: 4px;
          opacity: 80%`,
          });
        });
      });

    timelineRef.current.on('click', (properties) => {
      if (properties.what === 'item') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const selectedItem = items.get(properties.item) as any as DataItem;
        handleEdit(String(selectedItem.id));
      }
    });
    timelineRef.current.setGroups(groups);
    timelineRef.current.setItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryGroups, sortingOrder]);

  return (
    <div className="agenda-component__wrapper">
      <div className="agenda-component__agenda" ref={containerRef}></div>
      <div className="agenda-component__buttons">
        <Button
          value={sortingOrder === 'asc' ? '⬇️' : '⬆️'}
          onClick={() => setSortingOrder(sortingOrder === 'asc' ? 'desc' : 'asc')}
        />
        <Button value={'+'} onClick={() => timelineRef.current?.zoomIn(0.5)} />
        <Button value={'-'} onClick={() => timelineRef.current?.zoomOut(0.5)} />
      </div>
    </div>
  );
};
